import React, { useState } from "react";
import {
  APIFieldType,
  FieldType,
  FieldTypeType,
  getFieldTypeDisplayName,
} from "../../../services/helpers/types";
import Button from "../../atoms/Button/Button";
import TextField from "../../atoms/TextField/TextField";
import { addFieldRenderer } from "./helpers";
import { Checkbox } from "@mui/material";
import { createNewField } from "../../../services/api/fields.api";
import { useDispatch } from "react-redux";
import { sendToasterMessage } from "../../../services/helpers/commonHelpers";
import { MessageTypeEnum } from "../../../redux/reducers/messageReducer";
import { useTranslation } from "react-i18next";

type Mode = "add" | "edit";

interface AddFieldValuesFormProps {
  type: FieldTypeType;
  mode: Mode;
  tabID: number;
  position: number;
  field?: FieldType;
  onEditField?: (itemID: number, newField: APIFieldType) => void;
  onBack?: () => void;
  afterCreate?: (fieldID: number, newField: APIFieldType) => void;
}

const AddFieldValuesForm = (props: AddFieldValuesFormProps) => {
  const {
    type,
    mode,
    field,
    tabID,
    position,
    onEditField,
    onBack,
    afterCreate,
  } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [createObject, setCreateObject] = useState<APIFieldType>({
    tabID: tabID,
    fieldID: field?.fieldID ?? 0,
    name: field?.name ?? "",
    position: position,
    type: type,
    noteOption: true,
    defaultValue: field?.defaultValue ?? "",
    options: field?.options ?? [],
    unit: field?.unit,
  });

  const [name, setName] = useState(field?.name ?? "");
  const [noteOption, setNoteOption] = useState(field?.noteOption ?? true);

  const handleCreateOrUpdate = () => {
    if (name.length === 0) {
      sendToasterMessage(
        dispatch,
        t("nameCannotBeEmpty"),
        MessageTypeEnum.Error,
      );
      return;
    }
    if (mode === "edit") {
      onEditField?.(parseInt(createObject.fieldID.toString()), createObject);

      return;
    }

    if (
      createObject.type === FieldTypeType.ButtonGroup &&
      createObject?.options?.length === 0
    ) {
      const newCreateObject = {
        ...createObject,
        options: [
          {
            name: `${t("option")} 1`,
            value: `${t("option")} 1`,
            position: 1,
            optionID: 0,
            fieldID: 0,
          },
          {
            name: `${t("option")} 2`,
            value: `${t("option")} 2`,
            position: 2,
            optionID: 1,
            fieldID: 0,
          },
        ],
        defaultValue: `${t("option")} 1`,
      };

      createNewField(dispatch, newCreateObject).then((response) => {
        const newFieldID = response[0].fieldID as number;
        afterCreate?.(newFieldID, newCreateObject);
      });
      return;
    }

    if (createObject.type === FieldTypeType.Urine) {
      const newCreateObject = {
        ...createObject,
        defaultValue: t("negative").toLocaleLowerCase(),
      };
      createNewField(dispatch, newCreateObject).then((response) => {
        const newFieldID = response[0].fieldID as number;

        afterCreate?.(newFieldID, newCreateObject);
      });
      return;
    }

    if (
      createObject.type === FieldTypeType.Boolean &&
      createObject.defaultValue === ""
    ) {
      const createObjectWithDefaultValue = {
        ...createObject,
        defaultValue: t("no"),
      };
      createNewField(dispatch, createObjectWithDefaultValue).then(
        (response) => {
          const newFieldID = response[0].fieldID as number;

          afterCreate?.(newFieldID, createObjectWithDefaultValue);
        },
      );
      return;
    }

    createNewField(dispatch, createObject).then((response) => {
      const newFieldID = response[0].fieldID as number;
      afterCreate?.(newFieldID, createObject);
    });
  };

  const handleFieldChange = (
    name: string,
    value?: any,
    defaultValue?: string,
  ) => {
    let newDefaultValue =
      defaultValue !== undefined ? defaultValue : createObject.defaultValue;
    if (name === "defaultValue" && value) {
      newDefaultValue = value;
    }
    const newCreateObject = {
      ...createObject,
      [name]: value,
      defaultValue: newDefaultValue,
    };
    setCreateObject(newCreateObject);
  };

  const handleNameChange = (value: string) => {
    setName(value);
    handleFieldChange("name", value);
  };

  const handleNoteOptionChange = () => {
    setNoteOption(!noteOption);
    handleFieldChange("noteOption", !noteOption);
  };

  return (
    <div className="dui-add-field">
      <h1>{getFieldTypeDisplayName(t)[type]}</h1>
      <div className="dui-add-field-content dui-add-field-values">
        <div className="dui-example-field">
          <div className="dui-example-field-title">
            {t("fieldName")}: <span className="dui-required-field">*</span>
          </div>
          <TextField
            value={name}
            onChange={handleNameChange}
            placeholder={t("fieldNamePlaceholder")}
          />
        </div>
        {addFieldRenderer(type, handleFieldChange, field)}
        <div className="dui-example-field">
          <div className="dui-example-field-title">{t("allowNoteOption")}:</div>
          <Checkbox
            className="dui-checkbox"
            checked={noteOption}
            onChange={handleNoteOptionChange}
          />
        </div>
      </div>
      <div className="dui-add-field-actions">
        <Button onClick={onBack} type="text">
          {mode === "add" ? t("back") : t("cancel")}
        </Button>
        <Button onClick={handleCreateOrUpdate}>
          {mode === "add" ? t("addField") : t("editField")}
        </Button>
      </div>
    </div>
  );
};

export default AddFieldValuesForm;
